// src/components/profiles/filters/FilterLogic.js
export const applyFilters = (profiles, filters) => {
  if (!profiles || !profiles.length) {
    return { profiles: [], appliedFilters: filters };
  }

  let filteredProfiles = [...profiles];

  // Basic filters
  if (filters.gender && filters.gender !== 'all') {
    filteredProfiles = filteredProfiles.filter(profile => 
      profile.gender?.toLowerCase() === filters.gender.toLowerCase()
    );
  }

  if (filters.ageRange) {
    filteredProfiles = filteredProfiles.filter(profile => {
      if (!profile.age) return false;
      return profile.age >= filters.ageRange[0] && profile.age <= filters.ageRange[1];
    });
  }

  if (filters.city) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.city?.toLowerCase().includes(filters.city.toLowerCase())
    );
  }

  if (filters.profession) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.job_title?.toLowerCase().includes(filters.profession.toLowerCase())
    );
  }

  if (filters.religion) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.religion?.toLowerCase().includes(filters.religion.toLowerCase())
    );
  }

  if (filters.education) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.highest_degree?.toLowerCase().includes(filters.education.toLowerCase())
    );
  }

  if (filters.maritalStatus && filters.maritalStatus !== 'all') {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.marital_status?.toLowerCase() === filters.maritalStatus.toLowerCase()
    );
  }

  if (filters.verified) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.verified?.cnic && profile.verified?.education
    );
  }

  // Advanced filters
  if (filters.sect) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.sect?.toLowerCase().includes(filters.sect.toLowerCase())
    );
  }

  if (filters.caste) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.caste?.toLowerCase().includes(filters.caste.toLowerCase())
    );
  }

  if (filters.familyType) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.family_type?.toLowerCase() === filters.familyType.toLowerCase()
    );
  }

  if (filters.familyStatus) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.family_status?.toLowerCase().includes(filters.familyStatus.toLowerCase())
    );
  }

  if (filters.incomeRange) {
    filteredProfiles = filteredProfiles.filter(profile => {
      const income = profile.monthly_income_pkr || 0;
      return income >= filters.incomeRange[0] && income <= filters.incomeRange[1];
    });
  }

  if (filters.height) {
    filteredProfiles = filteredProfiles.filter(profile => {
      if (!profile.height) return false;
      const heightInInches = convertHeightToInches(profile.height);
      const filterHeight = filters.height;
      
      if (filterHeight === '4ft') return heightInInches < 60;
      if (filterHeight === '5ft') return heightInInches >= 60 && heightInInches < 66;
      if (filterHeight === '5ft5') return heightInInches >= 66 && heightInInches < 72;
      if (filterHeight === '6ft') return heightInInches >= 72;
      return true;
    });
  }

  if (filters.bodyType) {
    filteredProfiles = filteredProfiles.filter(profile =>
      profile.body_type?.toLowerCase() === filters.bodyType.toLowerCase()
    );
  }

  if (filters.hobbies && filters.hobbies.length > 0) {
    filteredProfiles = filteredProfiles.filter(profile => {
      if (!profile.hobbies || !profile.hobbies.length) return false;
      return filters.hobbies.some(hobby => 
        profile.hobbies.some(profileHobby => 
          profileHobby.toLowerCase().includes(hobby.toLowerCase())
        )
      );
    });
  }

  if (filters.languages && filters.languages.length > 0) {
    filteredProfiles = filteredProfiles.filter(profile => {
      if (!profile.languages_known || !profile.languages_known.length) return false;
      return filters.languages.some(language => 
        profile.languages_known.some(profileLanguage => 
          profileLanguage.toLowerCase().includes(language.toLowerCase())
        )
      );
    });
  }

  if (filters.lifestyleHabits && filters.lifestyleHabits.length > 0) {
    filteredProfiles = filteredProfiles.filter(profile => {
      const habits = [];
      if (profile.smoking === 'No') habits.push('Non-smoker');
      if (profile.drinking === 'No') habits.push('Non-drinker');
      if (profile.diet === 'Vegetarian') habits.push('Vegetarian');
      if (profile.diet === 'Non-vegetarian') habits.push('Non-vegetarian');
      if (profile.sports_interest && profile.sports_interest.length > 0) habits.push('Fitness');
      
      return filters.lifestyleHabits.every(habit => habits.includes(habit));
    });
  }

  // Self details filtering (for partner preferences)
  if (filters.selfDetails && filters.selfDetails.enabled) {
    filteredProfiles = filteredProfiles.filter(profile => {
      // Gender preference (opposite gender if self gender is specified)
      if (filters.selfDetails.gender) {
        const oppositeGender = filters.selfDetails.gender === 'male' ? 'female' : 'male';
        if (profile.gender !== oppositeGender) return false;
      }

      // Age preference (within self age range ± 5 years)
      if (filters.selfDetails.ageRange && profile.age) {
        const selfAge = (filters.selfDetails.ageRange[0] + filters.selfDetails.ageRange[1]) / 2;
        if (Math.abs(profile.age - selfAge) > 5) return false;
      }

      // Height preference
      if (filters.selfDetails.height) {
        const heightInInches = convertHeightToInches(profile.height);
        const selfHeight = filters.selfDetails.height;
        
        // For males: prefer similar or taller
        // For females: prefer similar or shorter
        if (filters.selfDetails.gender === 'male' && heightInInches < convertHeightToInches(selfHeight) - 2) {
          return false;
        }
        if (filters.selfDetails.gender === 'female' && heightInInches > convertHeightToInches(selfHeight) + 2) {
          return false;
        }
      }

      return true;
    });
  }

  return {
    profiles: filteredProfiles,
    appliedFilters: filters,
    stats: {
      total: filteredProfiles.length,
      male: filteredProfiles.filter(p => p.gender === 'male').length,
      female: filteredProfiles.filter(p => p.gender === 'female').length,
      verified: filteredProfiles.filter(p => p.verified?.cnic && p.verified?.education).length,
      averageAge: filteredProfiles.length > 0 
        ? Math.round(filteredProfiles.reduce((sum, p) => sum + (p.age || 0), 0) / filteredProfiles.length)
        : 0,
      averageIncome: filteredProfiles.length > 0
        ? Math.round(filteredProfiles.reduce((sum, p) => sum + (p.monthly_income_pkr || 0), 0) / filteredProfiles.length)
        : 0
    }
  };
};

const convertHeightToInches = (height) => {
  if (!height) return 0;
  
  const match = height.match(/(\d+)ft\s*(\d*)in?/i);
  if (match) {
    const feet = parseInt(match[1]);
    const inches = match[2] ? parseInt(match[2]) : 0;
    return feet * 12 + inches;
  }
  
  return 0;
};